import Pagination from "@mui/material/Pagination";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import useFavorites, { FavoriteSortingType } from "../../hooks/useFavorites";
import useFavoritesWithFocusArea from "../../hooks/useFavoritesWithFocusArea";
import usePagination from "../../hooks/usePagination";
import { useFavoriteStore, useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useSavedViewStore, useTenantInMarketStore } from "../../index";
import { formatNumber } from "../../utils/utils";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import FavoriteInfo from "./FavoriteInfo";

function Favorites() {
    const pipedriveStore = usePipedriveStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const savedViewStore = useSavedViewStore();
    const favoriteStore = useFavoriteStore();
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(FavoriteSortingType.AddedNewest);
    const [page, setPage] = useState(1);
    const favorites = useFavoritesWithFocusArea(useFavorites(debouncedSearchFilter, sortBy));
    const [pageCount, pagedFavorites] = usePagination(favorites, page, () => setPage(1));
    const isLoading = useMemo(() => computed(() =>
        pipedriveStore.isDealsLoading.fetching ||
        leadStore.isLeadsLoading.fetching ||
        leaseCompStore.isLeaseCompsLoading.fetching ||
        saleCompStore.isSaleCompsLoading.fetching ||
        tenantInMarketStore.isTenantsInMarketLoading.fetching ||
        savedViewStore.isSavedViewsLoading.fetching ||
        pipedriveStore.isOrganizationsLoading.fetching ||
        favoriteStore.isLoading
    ), []).get();

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!isLoading && formatNumber(favorites.length)} Favorites
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus placeholder="Search favorites.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={FavoriteSortingType.AddedNewest}>Newest</option>
                        <option value={FavoriteSortingType.AddedOldest}>Oldest</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {isLoading ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedFavorites.map(f => (
                        <FavoriteInfo
                            key={f.id}
                            targetId={f.targetId}
                            targetType={f.targetType}
                            className={tabStyles.item}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(Favorites);